import { Card } from "@/src/components/wrappers/Card";
import { findBoardingInformationForLocation } from "../../BookingDetails";
import { Booking, Operator } from "../../model";
import { Button } from "@/src/components/ui/button";
import { ExternalLink } from "lucide-react";
import { useState } from "react";
import { EditBookingBoardingInformationsModal } from "../../EditBookingBoardingInformations";
import { extractFromMultiName } from "../../../Locations/utils";

const BoardingSection = ({ booking, operator }: { booking: Booking; operator?: Operator }) => {
  const [updateBoardingInformations, setUpdateBoardingInformations] = useState(false);

  const handleDownload = () => {
    const boardingInfo = findBoardingInformationForLocation(
      booking.startLocation?.id ?? booking?.offerYacht?.startLocation?.id,
      operator?.boardingInfos || [],
    );

    const fileUrl = boardingInfo?.file?.url;
    const fileName = boardingInfo?.file?.name || "download";

    if (!fileUrl) {
      alert("No file available to download.");
      return;
    }

    // Trigger file download
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const boardingInfoForLocation = findBoardingInformationForLocation(
    booking?.startLocation?.id ?? booking?.offerYacht?.startLocation?.id,
    operator?.boardingInfos || [],
  )?.description;

  // Nothing for the start base? Show what the operator has for its other bases
  // rather than a bare "No data." — 169 staging bookings had operator boarding
  // information that simply didn't match the start location.
  const startLocationId = booking?.startLocation?.id ?? booking?.offerYacht?.startLocation?.id;
  const otherBases = (operator?.boardingInfos ?? []).filter(
    (info) => info.location?.id !== startLocationId && (info.description ?? "").trim(),
  );

  return (
    <Card className="w-full flex flex-col gap-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Boarding information</h3>
        <Button
          onClick={() => {
            setUpdateBoardingInformations(true);
          }}
          variant="link"
        >
          Edit
        </Button>{" "}
      </div>
      <div className="break-all">
        {booking?.boardingInformations || boardingInfoForLocation ? (
          <p>{booking?.boardingInformations || boardingInfoForLocation}</p>
        ) : otherBases.length ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-slate-500">
              No boarding information for this booking's start base. {operator?.name ?? "The operator"} has it for:
            </p>
            {otherBases.map((info) => (
              <div key={info.id} className="rounded-md bg-slate-50 px-3 py-2">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  {extractFromMultiName(info.location?.names ?? "") || "Other base"}
                </p>
                <p className="text-sm">{info.description}</p>
              </div>
            ))}
          </div>
        ) : (
          <p>No data.</p>
        )}

        {(booking?.startLocation?.id ?? booking?.offerYacht?.startLocation?.id) &&
        findBoardingInformationForLocation(booking?.startLocation?.id ?? booking?.offerYacht?.startLocation?.id, operator?.boardingInfos || [])?.file
          ?.name ? (
          <Button className="text-black p-0 text-sm font-medium text-blue-600" variant="link" onClick={handleDownload}>
            {
              findBoardingInformationForLocation(
                booking?.startLocation?.id ?? booking?.offerYacht?.startLocation?.id,
                // The booking query never selects offerYacht.yacht.operator, so
                // reading boardingInfos from there threw on any entry with a file.
                operator?.boardingInfos || [],
              )?.file?.name
            }
            <ExternalLink className="ml-1.5" size={20} />
          </Button>
        ) : null}
      </div>
      {updateBoardingInformations && (
        <EditBookingBoardingInformationsModal
          open={updateBoardingInformations}
          setOpen={setUpdateBoardingInformations}
          initialBoardingInformations={booking?.boardingInformations || boardingInfoForLocation || ""}
          bookingId={booking?.id}
        />
      )}
    </Card>
  );
};

export default BoardingSection;
